import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useAdvancedInsights } from '../../hooks/useAdvancedInsights';
import { InsightsSummaryCards } from './components/InsightsSummaryCards';
import { InsightsPatternsSection } from './components/InsightsPatternsSection';
import { InsightsCategorySection } from './components/InsightsCategorySection';
import { InsightsGoalsSection } from './components/InsightsGoalsSection';
import { InsightsCardsSection } from './components/InsightsCardsSection';
import { InsightsRecommendations } from './components/InsightsRecommendations';
import { InsightsHistoryChart } from './components/InsightsHistoryChart';
import { InsightsLockedBlock } from './components/InsightsLockedBlock';

const InsightsPage = () => {
  const perms = usePlanPermissions();
  const { data, loading } = useAdvancedInsights(
    perms.insightsLevel,
    perms.maxInsightsPerPeriod,
    perms.canAccessHistoricalInsights,
    perms.canAccessRecommendations,
  );

  const isBasic = perms.insightsLevel === 'basic';
  const isAdvanced = perms.insightsLevel === 'advanced' || perms.insightsLevel === 'full';

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
        <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!data) return null;

  const totalPatterns = 6;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-700 text-gray-900">Insights</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {isAdvanced
              ? 'Análise completa e aprofundada das suas finanças'
              : 'Resumo financeiro do mês atual'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isBasic ? (
            <span className="px-3 py-1.5 bg-amber-50 text-amber-600 border border-amber-200 rounded-full text-[12px] font-600 flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-star-line text-[13px]" />
              Versão básica
            </span>
          ) : (
            <span className="px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-full text-[12px] font-600 flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-vip-crown-line text-[13px]" />
              {perms.planName}
            </span>
          )}
        </div>
      </div>

      {/* Summary Cards — all plans */}
      <InsightsSummaryCards summary={data.summary} />

      {/* Patterns — limited for basic */}
      <InsightsPatternsSection
        patterns={data.patterns}
        isBasic={isBasic}
        totalAvailable={totalPatterns}
      />

      {/* Category Breakdown — all plans */}
      <InsightsCategorySection categories={data.categoryPatterns} />

      {/* Historical Chart — advanced+ */}
      {isAdvanced && perms.canAccessHistoricalInsights && data.monthlyTrends.length > 0 && (
        <InsightsHistoryChart trends={data.monthlyTrends} />
      )}
      {isBasic && (
        <InsightsLockedBlock
          title="Histórico dos últimos 6 meses"
          description="Visualize a evolução das suas receitas e despesas ao longo do tempo com o plano avançado."
          icon="ri-line-chart-line"
        />
      )}

      {/* Goals — advanced+ */}
      {isAdvanced ? (
        <InsightsGoalsSection goals={data.goalInsights} />
      ) : (
        <InsightsLockedBlock
          title="Análise detalhada de Metas"
          description="Veja projeções, ritmo de progresso e alertas personalizados para cada uma das suas metas."
          icon="ri-focus-3-line"
        />
      )}

      {/* Cards — advanced+ */}
      {isAdvanced && data.cardInsights.length > 0 && (
        <InsightsCardsSection cards={data.cardInsights} />
      )}
      {isBasic && (
        <InsightsLockedBlock
          title="Insights de Cartões & Faturas"
          description="Acompanhe o uso do limite, maiores gastos por cartão e alertas de fatura no plano avançado."
          icon="ri-bank-card-line"
        />
      )}

      {/* Recommendations — controlled by plan */}
      {perms.canAccessRecommendations && data.recommendations.length > 0 && (
        <InsightsRecommendations recommendations={data.recommendations} />
      )}
      {!perms.canAccessRecommendations && (
        <InsightsLockedBlock
          title="Recomendações Personalizadas"
          description="Receba sugestões de ações concretas para melhorar sua saúde financeira, baseadas nos seus dados reais."
          icon="ri-lightbulb-flash-line"
        />
      )}
    </div>
  );
};

export default InsightsPage;
